import {SystemStyleObject} from '@chakra-ui/react'
import tokens from 'airship-design-system/tokens.json'

export type Font = keyof typeof tokens.text.family

/* Transforms this:

  "text": {
    "family": {
      "base": {
        "value": "Inter",
        "type": "fontFamilies"
      },
      "monospace": {
        "value": "Fira Code",
        "type": "fontFamilies"
      }
      ...
    },
  ...

  Into:

    "base": "Inter",
    "monospace": "Fira Code"

*/
const fonts: Record<Font, string> = Object.keys(tokens.text.family).reduce(
  (obj, key) => {
    obj[key as Font] = tokens.text.family[key as Font].value
    return obj
  },
  {} as Record<Font, string>
)

export type FontWeight = keyof typeof tokens.text.weight

/* Transforms this:

  "text": {
    ...
    "weight": {
      "normal": {
        "value": 400,
        "type": "fontWeights"
      },
      "medium": {
        "value": 500,
        "type": "fontWeights"
      },
      ...
    }

  Into:

    "normal": 400,
    "medium": 500,
    ...

*/
const fontWeights: Record<FontWeight, number> = Object.keys(
  tokens.text.weight
).reduce((obj, key) => {
  obj[key as FontWeight] = tokens.text.weight[key as FontWeight].value
  return obj
}, {} as Record<FontWeight, number>)

export type TypographyContext = keyof typeof tokens.typography

// Unfortunately, we can't make a simple mapped type using the above
// because different `TypographyContext`s support different named sizes
export type FontSize = {
  display: keyof typeof tokens.typography.display.size
  heading: keyof typeof tokens.typography.heading.size
  subheading: keyof typeof tokens.typography.subheading.size
  body: keyof typeof tokens.typography.body.size
  codeblock: keyof typeof tokens.typography.codeblock.size
  caption: keyof typeof tokens.typography.caption.size
  meta: keyof typeof tokens.typography.meta.size
}

/* Transforms this:

  "typo": {
    "display": {
      "size": {
        "large": {
          "value": "40px",
          "type": "fontSizes"
        },
        "medium": {
          "value": "32px",
          "type": "fontSizes"
        },
        ...
      }
      ...
    },
    "heading": {
      "size": {
        "medium": {
          "value": "20px",
          "type": "fontSizes"
        }
      },
      ...
    ""

  Into:

    "display": {
      "large": "40px",
      "medium": "32px",
      ...
    },
    "heading": {
      "medium": "20px",
    },
    ...
*/
const fontSizes = Object.keys(tokens.typography).reduce((obj, key) => {
  obj[key as TypographyContext] = Object.keys(
    tokens.typography[key as TypographyContext].size
  ).reduce((innerObj, innerKey) => {
    const values = tokens.typography[key as TypographyContext].size
    innerObj[innerKey] =
      tokens.typography[key as TypographyContext].size[
        innerKey as keyof typeof values
      ].value
    return innerObj
  }, {} as Record<string, string>)
  return obj
}, {} as Record<TypographyContext, Record<string, string>>)

// Unfortunately, we can't make a simple mapped type using the above
// because different `TypographyContext`s support different named line heights
export type LineHeight = {
  display: keyof typeof tokens.typography.display.lineHeight
  heading: keyof typeof tokens.typography.heading.lineHeight
  subheading: keyof typeof tokens.typography.subheading.lineHeight
  body: keyof typeof tokens.typography.body.lineHeight
  codeblock: keyof typeof tokens.typography.codeblock.lineHeight
  caption: keyof typeof tokens.typography.caption.lineHeight
  meta: keyof typeof tokens.typography.meta.lineHeight
}

const lineHeights = Object.keys(tokens.typography).reduce((obj, key) => {
  obj[key as TypographyContext] = Object.keys(
    tokens.typography[key as TypographyContext].lineHeight
  ).reduce((innerObj, innerKey) => {
    const values = tokens.typography[key as TypographyContext].lineHeight
    innerObj[innerKey] =
      tokens.typography[key as TypographyContext].lineHeight[
        innerKey as keyof typeof values
      ].value
    return innerObj
  }, {} as Record<string, string>)
  return obj
}, {} as Record<TypographyContext, Record<string, string>>)

/* Transforms this:
  "typo": {
      "heading": {
        ...
        "medium": {
          "fontFamily": {
            "value": "Inter",
            "type": "fontFamilies"
          },
          "fontWeight": {
            "value": 700,
            "type": "fontWeights"
          },
          "lineHeight": {
            "value": "32px",
            "type": "lineHeight"
          },
          "fontSize": {
            "value": "20px",
            "type": "fontSizes"
          },
          "textCase": {
            "value": "none",
            "type": "textCase"
          }
        }
    },
  }

  Into:

  {
    "heading": {
      "medium": {
        "fontFamily": "Inter",
        "fontWeight": 700,
        "lineHeight": "32px",
        "fontSize": "20px",
        "textTransform": "none"
      }
    }
  }
 */
export type TextStyleValues = {
  fontFamily: {
    value: string
  }
  fontWeight: {
    value: number
  }
  lineHeight: {
    value: string
  }
  fontSize: {
    value: string
  }
  textCase: {
    value: string
  }
}
export const textStyleTokensToRecord = (
  value: TextStyleValues
): SystemStyleObject => {
  return Object.keys(value).reduce((obj, key) => {
    const objKey = key === 'textCase' ? 'textTransform' : key
    obj[objKey] = value[key as keyof TextStyleValues].value

    return obj
  }, {} as Record<string, string | number>)
}

export type TextStyles = Record<
  TypographyContext,
  Record<string, SystemStyleObject>
>
/**
 * @NOTE: Due to a bug: https://github.com/chakra-ui/chakra-ui/issues/3884
 * we can't use textStyles as documented: https://chakra-ui.com/docs/styled-system/text-and-layer-styles#text-styles
 *
 * As a workaround we instead create an object with the values from the tokens and
 * apply them in each component's theme by using the spread operator. For example
 * if the button used the body.medium text styles, in `ui/lib/design-system/theme/components/Button.ts`
 * We would apply that to the style definition:
 *
 * const baseStyle = defineStyle({
 *   ...typography.textStyles.body.medium,
 *   // other styles
 * }
 */
const textStyles: TextStyles = {
  body: {
    small: textStyleTokensToRecord(tokens.typography.body.small),
    medium: textStyleTokensToRecord(tokens.typography.body.medium),
    large: textStyleTokensToRecord(tokens.typography.body.large),
  },
  caption: {
    medium: textStyleTokensToRecord(tokens.typography.caption.medium),
  },
  codeblock: {
    medium: textStyleTokensToRecord(tokens.typography.codeblock.medium),
  },
  display: {
    small: textStyleTokensToRecord(tokens.typography.display.small),
    medium: textStyleTokensToRecord(tokens.typography.display.medium),
    large: textStyleTokensToRecord(tokens.typography.display.large),
  },
  heading: {
    medium: textStyleTokensToRecord(tokens.typography.heading.medium),
  },
  meta: {
    medium: textStyleTokensToRecord(tokens.typography.meta.medium),
  },
  subheading: {
    small: textStyleTokensToRecord(tokens.typography.subheading.small),
    medium: textStyleTokensToRecord(tokens.typography.subheading.medium),
    large: textStyleTokensToRecord(tokens.typography.subheading.large),
  },
}

const headingVariants = {
  displaySmall: textStyles.display.small ?? {},
  displayMedium: textStyles.display.small ?? {},
  displayLarge: textStyles.display.large ?? {},

  headingMedium: textStyles.heading.medium ?? {},

  subheadingSmall: textStyles.subheading.small ?? {},
  subheadingMedium: textStyles.subheading.medium ?? {},
  subheadingLarge: textStyles.subheading.large ?? {},
}

const textVariants = {
  bodySmall: textStyles.body.small ?? {},
  bodyMedium: textStyles.body.medium ?? {},
  bodyLarge: textStyles.body.large ?? {},

  captionMedium: textStyles.caption.medium ?? {},

  metaMedium: textStyles.meta.medium ?? {},

  codeBlock: textStyles.codeblock.medium ?? {},
}

const typography = {
  fontSizes,
  fontWeights,
  lineHeights,
  fonts,
  textStyles,
  headingVariants: headingVariants as Record<
    keyof typeof headingVariants,
    SystemStyleObject
  >,
  textVariants: textVariants as Record<
    keyof typeof textVariants,
    SystemStyleObject
  >,
} as const

export default typography
